import {useCallback, useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {sitesSelector} from "../store/entity/site/selectors";
import {useGetAllSitesQuery} from "../store/entity/site/site-store";
import {wellsSelector} from "../store/entity/well/selectors";
import {useGetAllWellsQuery} from "../store/entity/well/well-store";
import {useGetAllContainersQuery} from "../store/entity/container/container-store";
import {containersSelector} from "../store/entity/container/selectors";
import {SiteEntity} from "../model/site";
import {WellEntity} from "../model/well";
import {ContainerEntity} from "../model/container";

export type UseGetAllBaseEntitiesQueryProps = {
  regionId?: string
}

export type UseGetAllBaseEntitiesQueryResult = {
  entities: {
    sites?: SiteEntity[],
    wells?: WellEntity[],
    containers?: ContainerEntity[],
  }
  isLoading?: boolean,
  refetch?: any
}

export const useGetAllBaseEntitiesQuery = (props: UseGetAllBaseEntitiesQueryProps): UseGetAllBaseEntitiesQueryResult => {
  const {
    regionId,
  } = props;

  const selectSites = useMemo(() => {
    return sitesSelector()
  }, []);

  const {sites, isSitesLoading, refetch: sitesRefetch} = useGetAllSitesQuery(regionId ?? skipToken, {
    selectFromResult: (result) => ({
      ...result,
      sites: selectSites(result, {regionId: regionId}),
      isSitesLoading: result.isLoading,
    })
  });

  const selectWells = useMemo(() => {
    return wellsSelector()
  }, []);

  const {wells, isWellsLoading, refetch: wellsRefetch} = useGetAllWellsQuery(regionId ?? skipToken, {
    selectFromResult: (result) => ({
      ...result,
      wells: selectWells(result, {regionId: regionId}),
      isWellsLoading: result.isLoading,
    })
  });

  const selectContainers = useMemo(() => {
    return containersSelector()
  }, []);

  const {
    containers,
    isContainersLoading,
    refetch: containersRefetch
  } = useGetAllContainersQuery(regionId ?? skipToken, {
    selectFromResult: (result) => ({
      ...result,
      containers: selectContainers(result, {regionId: regionId}),
      isContainersLoading: result.isLoading,
    })
  });

  const isLoading = useMemo(() => {
    return isSitesLoading ||
      isWellsLoading ||
      isContainersLoading
  }, [
    isSitesLoading,
    isWellsLoading,
    isContainersLoading,
  ])

  const refetch = useCallback(() => {
    if (sitesRefetch) {
      sitesRefetch()
    }
    if (wellsRefetch) {
      wellsRefetch()
    }
    if (containersRefetch) {
      containersRefetch()
    }
  }, [
    sitesRefetch,
    wellsRefetch,
    containersRefetch,
  ])

  return {
    entities: {
      sites: sites,
      wells: wells,
      containers: containers,
    },
    isLoading: isLoading,
    refetch: refetch,
  };
};
